import { useEffect, useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";

const MAX = 6;
const MAX_MB = 10;

export const PhotoDrop = ({ files, setFiles }) => {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => { urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [files]);

  const add = (list) => {
    const imgs = Array.from(list || []).filter((f) => {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name} is not an image`); return false; }
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name} is over ${MAX_MB} MB`); return false; }
      return true;
    });
    const next = [...files, ...imgs].slice(0, MAX);
    if (files.length + imgs.length > MAX) toast.warning(`Up to ${MAX} photos per request`);
    setFiles(next);
  };

  return (
    <div>
      <div
        onClick={() => input.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        data-testid="photo-dropzone"
        className={`cursor-pointer border-2 border-dashed p-8 text-center transition-colors duration-300 ${drag ? "border-[var(--orange)] bg-[var(--orange)]/10" : "border-[var(--line)] hover:border-[var(--orange)]/60 bg-[#121418]"}`}
      >
        <ImagePlus className="w-8 h-8 mx-auto text-[var(--orange)]" />
        <p className="mt-3 font-display text-xl font-bold uppercase">Drop photos or tap to upload</p>
        <p className="mt-1 text-sm text-zinc-400">Boxes, labels, the space or the item · up to {MAX} images, {MAX_MB} MB each</p>
        <input ref={input} type="file" accept="image/*" multiple hidden data-testid="photo-input" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      </div>
      {previews.length > 0 && (
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="photo-previews">
          {previews.map((u, i) => (
            <div key={u} className="relative aspect-square overflow-hidden border border-[var(--line)]">
              <img src={u} alt={`Upload preview ${i + 1}`} className="w-full h-full object-cover" />
              <button type="button" aria-label="Remove photo" data-testid={`remove-photo-${i}`}
                onClick={() => setFiles(files.filter((_, j) => j !== i))}
                className="absolute top-1 right-1 w-6 h-6 grid place-items-center bg-[var(--ink)]/85 text-white hover:bg-[var(--orange)] hover:text-[var(--ink)] transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
